import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiRequest } from '@/lib/queryClient';
import { useAlert } from '@/hooks/use-alert';
import { useTranslation } from 'react-i18next';
import { useToast } from '@/hooks/use-toast';
import { API_ROUTES } from '@mangelapp/shared';

export const useOrganizationDocumentActions = (organizationId: string) => {
  const queryClient = useQueryClient();
  const { confirm } = useAlert();
  const { toast } = useToast();
  const { t } = useTranslation(['pages.listen', 'common', 'pages.projectDetails']);
  const [documentActionLoading, setDocumentActionLoading] = useState<string | null>(null);
  const [showDocumentUpload, setShowDocumentUpload] = useState(false);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [isUploadingDocument, setIsUploadingDocument] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  const [shareExpiresIn, setShareExpiresIn] = useState<number>(1);
  const [shareDocumentName, setShareDocumentName] = useState<string | undefined>(undefined);

  // Upload document mutation
  const uploadDocumentMutation = useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('organizationId', organizationId);
      formData.append('gewerk', 'Allgemein');

      // Use multipart upload to new endpoint
      const response = await apiRequest('/api/documents/upload-file', {
        method: 'POST',
        // Don't set Content-Type - browser will set it with boundary for multipart/form-data
        body: formData,
      });

      if (!response.ok) {
        throw new Error('Upload failed');
      }

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/documents/organization", organizationId] });
      setShowDocumentUpload(false);
      setUploadFile(null);
      setIsUploadingDocument(false);
    },
    onError: (error) => {
      console.error('Upload error:', error);
      setIsUploadingDocument(false);
    }
  });

  // Delete document mutation
  const deleteDocumentMutation = useMutation({
    mutationFn: async (documentId: string) => {
      const response = await apiRequest(`/api/documents/${documentId}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error('Delete failed');
      }

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/documents/organization", organizationId] });
    }
  });

  // Update document name mutation
  const updateDocumentMutation = useMutation({
    mutationFn: async ({ documentId, name }: { documentId: string; name: string }) => {
      const response = await apiRequest(`/api/documents/${documentId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ name }),
      });

      if (!response.ok) {
        throw new Error('Update failed');
      }

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/documents/organization", organizationId] });
      toast({
        title: t('messages.success.updated', { ns: 'pages.listen' }),
      });
    },
    onError: () => {
      toast({
        title: t('messages.error.update', { ns: 'pages.listen' }),
        variant: 'destructive',
      });
    },
  });

  const handleViewDocument = async (document: any) => {
    setDocumentActionLoading(`view-${document.id}`);
    
    // Open window immediately to avoid popup blocker (must be synchronous with user action)
    const newWindow = window.open('about:blank', '_blank');
    
    try {
      const response = await apiRequest(`/api/documents/${document.id}/file`);
      if (!response.ok) {
        throw new Error('Failed to fetch document');
      }
      
      // Parse JSON response containing the file URL
      const data = await response.json();
      
      if (!data.fileUrl) {
        throw new Error('No file URL returned');
      }
      
      // Navigate the already-opened window to the file URL directly
      if (newWindow) {
        newWindow.location.href = data.fileUrl;
      }
    } catch (error) {
      console.error('Error viewing document:', error);
      // Close the blank window if there was an error
      if (newWindow) {
        newWindow.close();
      }
    } finally {
      setDocumentActionLoading(null);
    }
  };

  const handleDownloadDocument = async (document: any) => {
    setDocumentActionLoading(`download-${document.id}`);
    try {
      const response = await apiRequest(`/api/documents/${document.id}/file`);
      if (!response.ok) {
        throw new Error('Failed to fetch document');
      }
      
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      
      // Create a temporary link and trigger download
      const link = window.document.createElement('a');
      link.href = url;
      link.download = document.fileName || document.name;
      window.document.body.appendChild(link);
      link.click();
      window.document.body.removeChild(link);
      
      // Clean up the URL after a delay
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (error) {
      console.error('Error downloading document:', error);
    } finally {
      setDocumentActionLoading(null);
    }
  };

  const handleDeleteDocument = async (documentId: string) => {
    const result = await confirm({
      message: t('documents.confirmDelete', { ns: 'pages.listen' }) || 'Are you sure you want to delete this document?',
      variant: 'destructive',
      confirmText: t('documents.delete', { ns: 'pages.listen' }) || t('alerts.delete', { ns: 'common' })
    });
    if (result) {
      deleteDocumentMutation.mutate(documentId);
    }
  };

  const handleUploadClick = () => {
    setShowDocumentUpload(true);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setUploadFile(file);
  };

  const handleDocumentUpload = () => {
    if (!uploadFile) return;
    
    setIsUploadingDocument(true);
    uploadDocumentMutation.mutate(uploadFile);
  };

  const handleUploadCancel = () => {
    setShowDocumentUpload(false);
    setUploadFile(null);
    setIsUploadingDocument(false);
  };

  // Connect document to projects mutation
  const connectToProjectsMutation = useMutation({
    mutationFn: async ({ documentId, projectIds }: { documentId: string; projectIds: string[] }) => {
      const response = await apiRequest(API_ROUTES.DOCUMENTS.CONNECT(documentId), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ projectIds }),
      });

      if (!response.ok) {
        throw new Error('Failed to connect document to projects');
      }

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/documents/organization", organizationId] });
      // Invalidate connected projects queries for all documents
      queryClient.invalidateQueries({ queryKey: ["/api/documents", "projects"] });
      queryClient.invalidateQueries({ queryKey: ["/api/documents", "connected-projects"] });
    },
  });

  // Disconnect document from project mutation
  const disconnectFromProjectMutation = useMutation({
    mutationFn: async ({ documentId, projectId }: { documentId: string; projectId: string }) => {
      const response = await apiRequest(API_ROUTES.DOCUMENTS.DISCONNECT(documentId, projectId), {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error('Failed to disconnect document from project');
      }

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/documents/organization", organizationId] });
      // Invalidate connected projects queries for all documents
      queryClient.invalidateQueries({ queryKey: ["/api/documents", "projects"] });
      queryClient.invalidateQueries({ queryKey: ["/api/documents", "connected-projects"] });
    },
  });


  const connectToProjects = (documentId: string, projectIds: string[]) => {
    connectToProjectsMutation.mutate({ documentId, projectIds });
  };

  const disconnectFromProject = (documentId: string, projectId: string) => {
    disconnectFromProjectMutation.mutate({ documentId, projectId });
  };

  const handleShareDocument = async (document: any) => {
    setDocumentActionLoading(`share-${document.id}`);
    try {
      const response = await apiRequest(`/api/documents/${document.id}/token`);
      if (!response.ok) {
        throw new Error('Failed to generate share token');
      }

      const data = await response.json();
      const token = data.data?.token || data.token;
      const expiresIn = data.data?.expiresIn || data.expiresIn || 3600; // Default 1 hour
      // Use canonical URL from backend when present (signed share flow)
      const shareableUrl =
        data.data?.url ||
        `${window.location.origin}/api/documents/${document.id}/view?token=${encodeURIComponent(token ?? '')}`;

      setShareUrl(shareableUrl);
      setShareExpiresIn(Math.floor(expiresIn / 3600)); // Convert seconds to hours
      setShareDocumentName(document.name || document.fileName);
      setShareDialogOpen(true);
    } catch (error) {
      console.error('Error generating share link:', error);
      toast({
        title: t('toasts.error', { ns: 'pages.projectDetails' }) || t('toasts.error', { ns: 'common' }),
        description: t('documents.shareError', { ns: 'pages.projectDetails' }) || 'Failed to generate share link',
        variant: 'destructive',
      });
    } finally {
      setDocumentActionLoading(null);
    }
  };

  const updateDocumentName = async (documentId: string, name: string) => {
    await updateDocumentMutation.mutateAsync({ documentId, name });
  };

  return {
    // State
    documentActionLoading,
    showDocumentUpload,
    uploadFile,
    isUploadingDocument,
    
    // Actions
    handleViewDocument,
    handleDownloadDocument,
    handleDeleteDocument,
    updateDocumentName,
    handleShareDocument,
    handleUploadClick,
    handleFileSelect,
    handleDocumentUpload,
    handleUploadCancel,
    connectToProjects,
    disconnectFromProject,
    shareDialogOpen,
    setShareDialogOpen,
    shareUrl,
    shareExpiresIn,
    shareDocumentName,
    
    // Mutations
    uploadDocumentMutation,
    deleteDocumentMutation,
    updateDocumentMutation,
    connectToProjectsMutation,
    disconnectFromProjectMutation,
  };
};
